import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import type { CharacterView, StoryLanguage } from "@/lib/games/roleplay/contract";
import { capitalized } from "@/lib/games/roleplay/story";
import { factLabel } from "./labels";

// The characters met or heard of (39), as the player view shows them: information only, nothing here acts in the story
// (30). Ids are per-game tokens, used only as keys; facts carry a kind and no number, keyed by position (§3.5). `note`
// sits in the header's corner, where the tutorial's guide marks its example cast as one. `fresh` names the characters
// who have just come into the story (82): each wears a neutral "new" beside their name until the player's next turn, as
// a new item does in the inventory. The game page holds whether it is open (`open`, `onToggle`; folded when a game
// begins), the guide's example opens by itself. It opens and closes like every Disclosure, on the side boxes' one
// surface (22).
//
// A character is named by the name fact once it is known, else by the authored description, which is written to be
// told mid-sentence ("der Mann aus Reihe 12"): as a heading it starts with a capital letter, the
// rest as authored. Under a known name the description stays exactly as authored.

function CarriedItems({ items, language }: { items: CharacterView["items"]; language: StoryLanguage }) {
  const names = items.known.map((item) => item.name[language]).join(", ");
  // 42: what a character carries before the player asked reads the same for everyone, and never with a count.
  if (!items.complete && items.known.length === 0) {
    return <FormattedMessage id="grow.roleplay.cast.itemsUnknown" defaultMessage="Items: unknown" />;
  }
  if (!items.complete) {
    return (
      <FormattedMessage
        id="grow.roleplay.cast.itemsMoreUnknown"
        defaultMessage="Items: {items}, more: unknown"
        values={{ items: names }}
      />
    );
  }
  if (items.known.length === 0) {
    return <FormattedMessage id="grow.roleplay.cast.itemsNone" defaultMessage="Carries nothing" />;
  }
  return <FormattedMessage id="grow.roleplay.cast.items" defaultMessage="Items: {items}" values={{ items: names }} />;
}

const NOBODY_FRESH: ReadonlySet<string> = new Set();

function Character({ character, language, fresh }: { character: CharacterView; language: StoryLanguage; fresh: boolean }) {
  const intl = useIntl();
  const placeholder = character.placeholder[language];
  const nameFact = character.facts.find((fact) => fact.kind === "name");
  const name = nameFact ? nameFact.value[language] : capitalized(placeholder, language);
  return (
    <li className="space-y-1 border-b border-ink/5 pb-3 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className="text-smycles-body font-medium">{name}</p>
        {fresh ? (
          <span className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="grow.roleplay.cast.new" defaultMessage="new" />
          </span>
        ) : null}
      </div>
      {nameFact ? <p className="smycles-field-meta text-ink-muted">{placeholder}</p> : null}
      {character.presence === "heard_of" ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="grow.roleplay.cast.heardOf" defaultMessage="known from hearsay" />
        </p>
      ) : null}
      {!character.available ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="grow.roleplay.cast.unavailable" defaultMessage="no longer available" />
        </p>
      ) : null}
      {character.shownMood ? (
        <p className="smycles-field-meta text-ink">
          <FormattedMessage
            id="grow.roleplay.cast.shownMood"
            defaultMessage="Seems {mood}"
            values={{ mood: character.shownMood[language] }}
          />
        </p>
      ) : null}
      <dl className="space-y-0.5 pt-1">
        {character.facts.map((fact, index) =>
          fact.kind === "name" ? null : (
            <div key={index} className="flex flex-wrap gap-x-2">
              <dt className="smycles-field-label">{factLabel(intl, fact.kind)}</dt>
              <dd className="smycles-field-meta text-ink">{fact.value[language]}</dd>
            </div>
          ),
        )}
      </dl>
      <p className="smycles-field-meta text-ink-muted">
        <CarriedItems items={character.items} language={language} />
      </p>
    </li>
  );
}

export function CastPanel({
  cast,
  language,
  note,
  fresh = NOBODY_FRESH,
  open,
  onToggle,
}: {
  cast: readonly CharacterView[];
  language: StoryLanguage;
  note?: ReactNode;
  fresh?: ReadonlySet<string>;
  open?: boolean;
  onToggle?: () => void;
}) {
  return (
    <Disclosure
      title={<FormattedMessage id="grow.roleplay.cast.title" defaultMessage="Characters" />}
      count={cast.length || null}
      headerAction={note ? <span className="smycles-field-meta text-ink-muted">{note}</span> : undefined}
      defaultOpen
      open={open}
      onToggle={onToggle}
      className="bg-surface"
    >
      {cast.length === 0 ? (
        <p className="smycles-field-meta text-ink-muted">
          <FormattedMessage id="grow.roleplay.cast.empty" defaultMessage="You have not met anyone yet." />
        </p>
      ) : (
        <ul className="space-y-3">
          {cast.map((character) => (
            <Character key={character.id} character={character} language={language} fresh={fresh.has(character.id)} />
          ))}
        </ul>
      )}
    </Disclosure>
  );
}
